import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "count"];

  connect() {
    this.fileList = [];
    this.previousFileListLength = 0;

    // Attach listeners to the element itself (which is the label)
    this.element.addEventListener("dragenter", this.showDropText.bind(this));
    this.element.addEventListener("dragleave", this.hideDropText.bind(this));
    this.element.addEventListener("dragover", this.preventDragDefault.bind(this));
    this.element.addEventListener("drop", this.handleDrop.bind(this));
    this.fileInputTarget.addEventListener("change", this.handleDrop.bind(this));

    // Listen for custom event dispatched when a document is deleted
    this.element.addEventListener("decrement-count", this.decrementCount.bind(this));
  }

  disconnect() {
    this.element.removeEventListener("decrement-count", this.decrementCount.bind(this));
  }

  // Called when a document is deleted via Turbo Stream
  decrementCount() {
    if (this.hasCountTarget) {
      const currentCount = parseInt(this.countTarget.innerText);
      const newCount = Math.max(0, currentCount - 1);
      this.countTarget.innerText = newCount;
    }
  }

  showDropText(event) {
    event.preventDefault();
    event.stopPropagation();
    this.element.classList.add("bg-blue-50", "border-blue-300");
  }

  hideDropText(event) {
    event.preventDefault();
    event.stopPropagation();
    this.element.classList.remove("bg-blue-50", "border-blue-300");
  }

  preventDragDefault(event) {
    event.preventDefault();
    event.stopPropagation();
    this.element.classList.add("bg-blue-50", "border-blue-300");
  }

  handleDrop(event) {
    event.preventDefault();
    const files = event.type === "drop" ? event.dataTransfer.files : event.target.files;
    if (files.length > 0) {
      let addedCount = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // Check if file already exists in the list (by name and size)
        const isDuplicate = this.fileList.some(existingFile =>
          existingFile.name === file.name && existingFile.size === file.size
        );

        if (!isDuplicate) {
          this.fileList.push(file);
          addedCount++;
        }
      }

      if (addedCount > 0) {
        this.updateFileList();
        this.submitForm();
      }
      this.hideDropText(event);
    }
  }

  // Saves users from having to scroll to the bottom and click "Save" after
  // every drop. The slide form's hidden file input is already populated by
  // updateFileList() before this runs.
  submitForm() {
    const form = this.element.closest("form");
    if (form) form.requestSubmit();
  }

  removeFile(event) {
    event.preventDefault();
    event.stopPropagation();

    // Get the document position from the clicked element or its parents
    let id = event.target.dataset.documentPosition ||
             event.target.parentElement?.dataset.documentPosition ||
             event.target.parentElement?.parentElement?.dataset.documentPosition;

    if (id !== undefined) {
      // Remove the file from the list
      this.fileList.splice(parseInt(id), 1);

      // Update all UI elements (count, pending list, file input, etc.)
      this.updateFileList();
    }
  }

  updateFileList() {
    // Update the visual file list if it exists (used in _upload_documents_field.slim)
    const fileListElement = this.element.querySelector("#file-list")
    if (fileListElement) {
      fileListElement.innerHTML = "";
      this.fileList.forEach((file, index) => {
        const listItem = document.createElement("span");
        listItem.classList.add("text-sm", "text-slate-400", "py-1", "flex", "gap-2", `file-${index}`);
        listItem.innerHTML = `
          <i class="fa fa-solid fa-trash text-slate-400 hover:text-red-600" data-document-position=${index} data-action="click->drop-attachments#removeFile:stop:prevent"></i>
          ${file.name}
        `;
        fileListElement.appendChild(listItem);
      });
    }

    // Show files in the "Attached Documents" section if it exists
    this.updateAttachedDocumentsSection();

    // Update the file input with the new file list
    const newFileList = new DataTransfer();
    this.fileList.forEach((file) => {
      newFileList.items.add(file);
    });
    this.fileInputTarget.files = newFileList.files;

    // Update count badge if it exists
    if (this.hasCountTarget) {
      // Use current displayed count, not the original dataset.count
      const currentCount = parseInt(this.countTarget.innerText) || parseInt(this.countTarget.dataset.count);
      // Calculate base count (current count minus any previously added files)
      const baseCount = currentCount - this.previousFileListLength || 0;
      const newCount = baseCount + this.fileList.length;
      this.countTarget.innerText = newCount;
      this.previousFileListLength = this.fileList.length;
    }
  }

  updateAttachedDocumentsSection() {
    // Find or create the pending documents container
    const form = this.element.closest('form');
    if (!form) return;

    let pendingContainer = form.querySelector('#pending-documents-container');

    if (this.fileList.length > 0) {
      // Create container if it doesn't exist
      if (!pendingContainer) {
        const documentSection = form.querySelector('.space-y-2')?.parentElement;
        if (!documentSection) return;

        const container = document.createElement('div');
        container.id = 'pending-documents-container';
        container.className = 'mb-6';
        container.innerHTML = `
          <h3 class="text-sm font-semibold text-gray-900 mb-3">Pending Attachments (not yet saved)</h3>
          <div class="space-y-2" id="pending-documents-list"></div>
        `;

        // Insert before the existing documents section or at the beginning
        if (documentSection) {
          documentSection.parentElement.insertBefore(container, documentSection);
        }
        pendingContainer = container;
      }

      // Update the pending documents list
      const pendingList = pendingContainer.querySelector('#pending-documents-list');
      if (pendingList) {
        pendingList.innerHTML = '';
        this.fileList.forEach((file, index) => {
          const docElement = document.createElement('div');
          docElement.className = 'flex items-center justify-between py-2 px-3 bg-blue-50 rounded border border-blue-200';

          const fileInfo = document.createElement('div');
          fileInfo.className = 'flex items-center gap-2';
          fileInfo.innerHTML = `
            <i class="fa fa-file-alt text-blue-400"></i>
            <span class="text-sm text-gray-700">${file.name}</span>
            <span class="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Pending</span>
          `;

          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'text-red-600 hover:text-red-800';
          deleteBtn.innerHTML = '<i class="fa fa-trash"></i>';
          deleteBtn.dataset.documentPosition = index;
          deleteBtn.addEventListener('click', this.removeFile.bind(this));

          docElement.appendChild(fileInfo);
          docElement.appendChild(deleteBtn);
          pendingList.appendChild(docElement);
        });
      }
    } else if (pendingContainer) {
      // Remove the container if no pending files
      pendingContainer.remove();
    }
  }
}

